import { Check, ChevronsUpDown, Layers } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS } from '@portal/contracts';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { useBusiness } from '@/lib/business';

/** Menu body shared by the sidebar control and the phone top bar: businesses, then legal entities. */
function SwitcherMenu({ side }: { side: 'bottom' | 'right' }) {
  const { businessId, memberships, setBusinessId, context, taxpayers, taxpayerScope, setTaxpayerScope } = useBusiness();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (
    <DropdownMenuContent className="w-72" side={side} align="start" sideOffset={6}>
      {memberships.length > 1 && (
        <>
          <DropdownMenuLabel className="text-xs text-muted-foreground">Businesses</DropdownMenuLabel>
          {memberships.map((m) => (
            <DropdownMenuItem
              key={m.businessId}
              onSelect={() => {
                if (m.businessId === businessId) return;
                setBusinessId(m.businessId);
                qc.removeQueries({ predicate: (q) => q.queryKey[0] === 'b' && q.queryKey[1] !== m.businessId });
                navigate('/');
              }}
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">{m.businessName}</span>
                <span className="text-xs text-muted-foreground">{ROLE_LABELS[m.role]}</span>
              </div>
              {m.businessId === businessId && <Check className="size-4" aria-label="Current" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuLabel className="text-xs text-muted-foreground">Legal entities in {context?.business.displayName ?? 'this business'}</DropdownMenuLabel>
      {taxpayers.length > 1 && (
        <DropdownMenuItem onSelect={() => setTaxpayerScope('all')}>
          <Layers className="size-4" aria-hidden />
          <span className="flex-1">All entities</span>
          {taxpayerScope === 'all' && <Check className="size-4" aria-label="Current" />}
        </DropdownMenuItem>
      )}
      {taxpayers.map((t) => (
        <DropdownMenuItem key={t.id} onSelect={() => setTaxpayerScope(t.id)}>
          <span className="flex-1 truncate">{t.legalName}</span>
          {(taxpayerScope === t.id || taxpayers.length === 1) && <Check className="size-4" aria-label="Current" />}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );
}

/** Phone top bar: the active business is always visible and tappable (the sidebar is hidden there). */
export function TopbarBusiness() {
  const { businessId, memberships, taxpayers, taxpayerScope } = useBusiness();
  const current = memberships.find((m) => m.businessId === businessId);
  const scope = taxpayerScope === 'all' ? (taxpayers.length > 1 ? `All ${taxpayers.length} entities` : null) : taxpayers.find((t) => t.id === taxpayerScope)?.legalName;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="min-w-0 max-w-[55%] gap-1.5 px-1.5 md:hidden">
          <span className="truncate font-medium">{current?.businessName ?? 'Business'}</span>
          {scope && <span className="hidden truncate text-xs text-muted-foreground min-[420px]:inline">{scope}</span>}
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="sr-only">Switch business</span>
        </Button>
      </DropdownMenuTrigger>
      <SwitcherMenu side="bottom" />
    </DropdownMenu>
  );
}

/** Switch business (a user may belong to several) and narrow to one taxpayer legal entity. */
export function BusinessSwitcher() {
  const { businessId, memberships, taxpayers, taxpayerScope } = useBusiness();
  const { isMobile } = useSidebar();
  const current = memberships.find((m) => m.businessId === businessId);
  const scopeLabel = taxpayerScope === 'all' ? (taxpayers.length > 1 ? `All ${taxpayers.length} entities` : (taxpayers[0]?.legalName ?? '')) : taxpayers.find((t) => t.id === taxpayerScope)?.legalName;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-border/50">
              <div aria-hidden className="flex size-7 shrink-0 items-center justify-center rounded-md border bg-card text-xs font-semibold text-foreground">
                {(current?.businessName ?? 'B').slice(0, 1).toUpperCase()}
              </div>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-medium text-foreground">{current?.businessName ?? 'Business'}</span>
                <span className="truncate text-xs text-muted-foreground">{scopeLabel}</span>
                <span className="sr-only">Switch business</span>
              </div>
              <ChevronsUpDown className="ml-auto size-3.5 text-muted-foreground" aria-hidden />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <SwitcherMenu side={isMobile ? 'bottom' : 'right'} />
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
